/**
 * §94 (M49) — dönem karşılaştırması. SAF fonksiyonlar: panel rozetleri ve
 * e-posta raporu AYNI eşiklerle aynı kararı verir — "%1 eşiği" iki yerde iki
 * kez yazılmaz (§94.3).
 *
 * §94.2 dürüstlük kuralları burada kod olur:
 *   · önceki dönem payda 0 ise Δ YOKTUR (null) — `∞` / "%+100" uydurması yasak,
 *   · önceki dönemin veri kapsamı dönem uzunluğunun %80'inin altındaysa
 *     karşılaştırma hiç yapılmaz (kısmi dönemi tam dönemle kıyaslamak sistematik
 *     "harika büyüme" yalanı üretir),
 *   · |Δ| < %1 "yaklaşık aynı"dır — gürültüyü ok gibi göstermek karar bozar.
 */

/** Karşılaştırılan metrik kimlikleri — §94.1 (aktif bulgu HARİÇ: stok, akış değil). */
export const COMPARE_METRICS = ['sessions', 'users', 'cr', 'revenue', 'conversions'] as const;
export type CompareMetric = (typeof COMPARE_METRICS)[number];

/**
 * §94.2 — kutupluluk tablosu: artışı "iyi" olmayan bir metrik eklendiğinde
 * (örn. hata oranı) rozet rengi bu tablodan gelir; unutulursa derleme kırılır.
 */
export const METRIC_POLARITY: Record<CompareMetric, 'higherIsBetter' | 'lowerIsBetter'> = {
  sessions: 'higherIsBetter',
  users: 'higherIsBetter',
  cr: 'higherIsBetter',
  revenue: 'higherIsBetter',
  conversions: 'higherIsBetter',
};

/** |Δ%| bu eşiğin altındaysa yön "flat"tır. */
export const FLAT_THRESHOLD_PCT = 1;

/** §94.2 — önceki dönemin kapsanan gün sayısı dönem uzunluğunun %80'ini bulmalı. */
export const COVERAGE_MIN_RATIO = 0.8;

export interface PeriodDelta {
  /** Yüzde değişim, tek ondalık (12.4 = %+12,4). */
  pct: number;
  direction: 'up' | 'down' | 'flat';
}

/**
 * Tek metriğin Δ'sı. `null` = karşılaştırma YOK (önceki dönem 0/negatif payda) —
 * çağıran rozeti hiç çizmez, "0'dan X'e" hikâyesi uydurmaz.
 */
export function comparePeriods(current: number, previous: number): PeriodDelta | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous <= 0) return null;
  const pct = Math.round(((current - previous) / previous) * 1000) / 10;
  if (Math.abs(pct) < FLAT_THRESHOLD_PCT) return { pct, direction: 'flat' };
  return { pct, direction: pct > 0 ? 'up' : 'down' };
}

/** §94.2 kapsam kuralı — kapsanan gün sayısı yeterli mi? */
export function previousCoverageOk(coveredDays: number, periodDays: number): boolean {
  if (periodDays <= 0) return false;
  return coveredDays / periodDays >= COVERAGE_MIN_RATIO;
}

/** Rozet metni: `▲ %12,4` biçiminin sayı kısmı (yerelleştirme çağıranda). */
export function deltaLabel(delta: PeriodDelta): string {
  const isaret = delta.direction === 'down' ? '-' : '+';
  return `${isaret}%${Math.abs(delta.pct).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`;
}
